import { ChangeDetectionStrategy, Component, inject, input, signal, computed, OnInit } from '@angular/core';
import { HealthGlanceComponent } from './health-glance/health-glance.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, switchMap, startWith } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import {
    IntegrationHealthSummary,
    IntegrationStatusHealth,
    IntegrationStatusHealthSummaryFilter,
    IntegrationStatusHealtOverTime
} from '../../models/integration-status-health.model';
import { IntegrationStatus, integrationStatusColors } from '../../enums/integration-status.enum';
import { HealthOverTimeComponent } from './health-over-time/health-over-time.component';
import { ClientIdentifiersVersionValues, RunIntegrationUtils } from '../run-integration/utils';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export const DefaultDateRange = 7;

@UntilDestroy()
@Component({
    selector: 'nova-dashboard-health',
    standalone: true,
    imports: [
        MatExpansionModule,
        MatSelectModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        HealthGlanceComponent,
        HealthOverTimeComponent
    ],
    templateUrl: './dashboard-health.component.html',
    styleUrls: ['./dashboard-health.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardHealthComponent implements OnInit {
    defaultClientName = 'All Clients';
    managerService = inject(IntegrationManagerService);

    identifiers = input.required<ClientIdentifiersVersionValues>();

    clientNames = computed(() => {
        const allClients = RunIntegrationUtils.getClientNames(this.identifiers());
        return [this.defaultClientName, ...allClients];
    });

    form = new FormGroup({
        clientName: new FormControl(this.defaultClientName),
        dateRange: new FormControl(DefaultDateRange)
    });

    healthData = signal<IntegrationStatusHealth | null>(null);

    ngOnInit() {
        this.form.valueChanges
            .pipe(
                startWith({
                    clientName: this.defaultClientName,
                    dateRange: DefaultDateRange
                }),
                switchMap((value) => {
                    const filter: IntegrationStatusHealthSummaryFilter = {
                        clientName: value.clientName || this.defaultClientName,
                        dateRange: value.dateRange || DefaultDateRange
                    };
                    return this.getData(filter);
                }),
                untilDestroyed(this)
            )
            .subscribe((health) => {
                this.healthData.set(health);
            });
    }

    dateRanges = [
        {
            text: 'Last 7 days',
            value: DefaultDateRange
        },
        {
            text: 'Last 30 days',
            value: 30
        },
        {
            text: 'Last 60 days',
            value: 60
        },
        {
            text: 'Last 90 days',
            value: 90
        },
        {
            text: 'Last 180 days',
            value: 180
        },
        {
            text: 'Last 365 days',
            value: 365
        }
    ];

    statusColors = integrationStatusColors;

    getXAxisLabel(days: number): string {
        return days < 31 ? 'Day' : days < 91 ? 'Week Start' : 'Month';
    }

    transformPartial = (status: string): string => (status === 'PartialSuccess' ? 'Partial Success' : status);

    getData = (filter: IntegrationStatusHealthSummaryFilter): Observable<IntegrationStatusHealth> => {
        if (filter.clientName === this.defaultClientName) {
            filter.clientName = '';
        }
        return this.managerService.getIntegrationStatusHealth(filter);
    };

    healthSummary = computed((): IntegrationHealthSummary | null => {
        const health = this.healthData();
        if (!health) {
            return null;
        }

        return {
            summary: health.summary.summary.map((value) => {
                const status = IntegrationStatus[+(value.status ?? 0)];
                return {
                    count: value.count,
                    status: this.transformPartial(status),
                    color: this.statusColors[+(value.status ?? 0)] as string
                };
            }),
            total: health.summary.total
        };
    });

    healthOverTime = computed((): IntegrationStatusHealtOverTime | null => {
        const health = this.healthData();
        if (!health) {
            return null;
        }

        // Get current form values for date range calculation
        const currentFormValue = this.form.value;
        const dateRange = currentFormValue.dateRange || DefaultDateRange;

        return {
            ...health.healtOverTime,
            details: health.healtOverTime.details.map((value) => {
                const status = IntegrationStatus[value.status ?? 0] as string;
                return {
                    counts: value.counts,
                    status: this.transformPartial(status),
                    color: this.statusColors[value.status] as string
                };
            }),
            total: health.summary.total,
            xAxisLabel: this.getXAxisLabel(dateRange)
        };
    });
}
